import { Alert, Button, Empty, Modal, Spin, Tag, Typography } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { useQueries } from '@tanstack/react-query';
import { posService } from '@/services/pos.service';
import { usePosShiftStoreId } from '@/utils/posShift';
import type { PosWarehouseStock } from '../types';
import { WarehouseSearchInput, filterWarehouses, useWarehouseKeyword } from './WarehouseSearchInput';

const { Text } = Typography;

export interface ComboWarehouseMember {
  itemId: string;
  variantId: string;
  productName: string;
  /** SL cần của riêng thành phần này (`member.quantity`) — mỗi thành phần một
   *  mức khác nhau (comboUnitQty × comboUnitCount), không phải một số chung. */
  requiredQty: number;
  currentWarehouseId?: string;
}

interface ComboWarehousePickerDialogProps {
  open: boolean;
  onClose: () => void;
  comboName: string;
  /** Chỉ những thành phần `canPickWarehouse` — cấp gọi lọc trước khi truyền vào. */
  members: ComboWarehouseMember[];
  /** Scope kho theo store — cùng ý nghĩa với `WarehousePickerDialog.storeId`. */
  storeId?: string;
  /**
   * TCR-2292: dùng lại dialog cho "Chọn kho cho tất cả mã hàng trong giỏ" —
   * `members` khi đó là mọi dòng chọn được kho của giỏ, không còn là thành phần
   * của 1 cụm. Mặc định giữ nguyên chữ của luồng combo.
   */
  title?: string;
  /** Danh từ đếm trong câu hướng dẫn: "thành phần" (combo) | "mã hàng" (cả giỏ). */
  itemNoun?: string;
  /** Nhãn tag "đủ hết" + nút bỏ chọn: "cả cụm" (combo) | "cả giỏ" (cả giỏ). */
  scopeNoun?: string;
  /**
   * `warehouse=null` → "Bỏ chọn kho" cho MỌI thành phần (an toàn, không cần
   * soi tồn vì xoá thì không thể vượt tồn).
   *
   * `warehouse` có giá trị → CHỈ áp cho các thành phần ĐỦ TỒN tại kho đó
   * (`fulfilledItemIds`). Thành phần không đủ tồn (`insufficientItemIds`) GIỮ
   * NGUYÊN kho hiện tại (thường là chưa chọn) — KHÔNG gán bừa rồi để BE từ
   * chối lúc thanh toán; cờ lỗi đỏ "Bắt buộc chọn kho xuất hàng" (nếu thiếu)
   * vẫn hiện đúng ở dòng thành phần đó, thu ngân tự chọn kho riêng qua nút
   * "📦 Chọn kho" của chính dòng đó.
   */
  onApply: (
    warehouse: PosWarehouseStock | null,
    result: { fulfilledItemIds: string[]; insufficientItemIds: string[] },
  ) => void;
}

/**
 * TCR-629 mở rộng cho Combo ảo: chọn 1 kho ở dòng ĐẦU CỤM, áp cho mọi thành
 * phần bên dưới cùng lúc — thay vì thu ngân phải bấm "Chọn kho" từng dòng.
 *
 * "Đủ tồn ở cấp cụm" KHÔNG phải nhị phân — mỗi thành phần cần SL khác nhau
 * (`comboUnitQty` khác nhau) nên một kho có thể đủ cho SP này nhưng thiếu SP
 * kia. Dialog phân loại từng kho theo 3 mức (đủ hết / đủ một phần / không đủ
 * ai) và luôn cho phép chọn TRỪ KHI không thành phần nào hưởng lợi — giống hệt
 * `WarehousePickerDialog` chỉ chặn click khi "chọn xong cũng vô nghĩa".
 */
export function ComboWarehousePickerDialog({
  open,
  onClose,
  comboName,
  members,
  storeId,
  title = 'Chọn kho xuất hàng cho cả cụm combo',
  itemNoun = 'thành phần',
  scopeNoun = 'cả cụm',
  onApply,
}: ComboWarehousePickerDialogProps) {
  const uniqueVariantIds = Array.from(new Set(members.map((m) => m.variantId)));

  // TCR-980: store hiệu lực phải nằm TRONG query key, nếu không đổi chi nhánh
  // sẽ trả lại cache kho của chi nhánh cũ. Xem comment ở `WarehousePickerDialog`.
  const shiftStoreId = usePosShiftStoreId();
  const effectiveStoreId = storeId ?? shiftStoreId ?? undefined;

  // Cùng queryKey shape với `WarehousePickerDialog` (['pos-warehouse-stocks',
  // variantId, effectiveStoreId]) — chia sẻ cache nếu thu ngân vừa mở picker
  // của riêng 1 thành phần rồi đóng lại để mở picker cấp cụm (hoặc ngược lại).
  const queries = useQueries({
    queries: uniqueVariantIds.map((variantId) => ({
      queryKey: ['pos-warehouse-stocks', variantId, effectiveStoreId],
      queryFn: () => posService.listVariantWarehouseStocks(variantId, storeId),
      enabled: open,
      staleTime: 10_000,
      retry: false,
    })),
  });

  const isFetching = queries.some((q) => q.isFetching);

  const stocksByVariant = new Map<string, PosWarehouseStock[]>();
  uniqueVariantIds.forEach((variantId, idx) => {
    stocksByVariant.set(variantId, queries[idx]?.data?.items ?? []);
  });
  // TCR-1205 — cùng cảnh báo dữ liệu kho lệch như WarehousePickerDialog, gộp
  // theo TỔNG các thành phần trong cụm (mỗi thành phần có thể lệch khác nhau).
  const unmatchedAvailable = queries.reduce(
    (sum, q) => sum + (q.data?.unmatched_available ?? 0),
    0,
  );

  function qtyAt(warehouseId: string, variantId: string): number {
    return (
      stocksByVariant.get(variantId)?.find((w) => w.warehouse_id === warehouseId)?.available ?? 0
    );
  }

  // Danh sách kho = union theo warehouse_id trên mọi thành phần — cùng 1 store
  // nên thường là cùng bộ kho, chỉ khác `quantity` theo từng biến thể.
  const warehouseMap = new Map<string, PosWarehouseStock>();
  stocksByVariant.forEach((list) => {
    list.forEach((w) => {
      if (!warehouseMap.has(w.warehouse_id)) warehouseMap.set(w.warehouse_id, w);
    });
  });
  const warehouses = Array.from(warehouseMap.values());
  const [keyword, setKeyword] = useWarehouseKeyword(open);
  const visibleWarehouses = filterWarehouses(warehouses, keyword);

  function handlePick(w: PosWarehouseStock) {
    const fulfilledItemIds: string[] = [];
    const insufficientItemIds: string[] = [];
    for (const m of members) {
      if (qtyAt(w.warehouse_id, m.variantId) >= m.requiredQty) {
        fulfilledItemIds.push(m.itemId);
      } else {
        insufficientItemIds.push(m.itemId);
      }
    }
    // Không thành phần nào hưởng lợi — chọn kho này không có tác dụng gì, giữ
    // dialog mở để thu ngân thử kho khác (cùng ngữ nghĩa "insufficient" chặn
    // click của WarehousePickerDialog gốc).
    if (fulfilledItemIds.length === 0) return;
    onApply(w, { fulfilledItemIds, insufficientItemIds });
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      width={560}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Hủy
        </Button>,
        <Button
          key="clear"
          danger
          onClick={() =>
            onApply(null, { fulfilledItemIds: members.map((m) => m.itemId), insufficientItemIds: [] })
          }
        >
          {`Bỏ chọn kho cho ${scopeNoun}`}
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 8 }}>
        <Text strong>{comboName}</Text>
        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
          {members.length} {itemNoun} cần chọn kho — chỉ {itemNoun} đủ tồn tại kho vừa chọn mới
          được gán, {itemNoun} còn thiếu tồn giữ nguyên để chọn riêng.
        </Text>
        <ul style={{ margin: '4px 0 0', paddingLeft: 18, maxHeight: 120, overflowY: 'auto' }}>
          {members.map((m) => (
            <li key={m.itemId} style={{ fontSize: 12, color: '#666' }}>
              {m.productName} — cần {m.requiredQty}
            </li>
          ))}
        </ul>
      </div>

      {unmatchedAvailable > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          message="Dữ liệu kho chưa khớp"
          description={`Hệ thống ghi nhận còn ${unmatchedAvailable} sản phẩm (gộp các thành phần) tồn ở kho không còn thuộc chi nhánh này — kho đã được chuyển sang chi nhánh khác hoặc đã bị xoá. Đây KHÔNG phải hết hàng, nhưng số tồn đó chưa bán được tại đây: báo quản lý kho kiểm tra và điều chỉnh.`}
        />
      )}

      <WarehouseSearchInput open={open} value={keyword} onChange={setKeyword} />

      <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 6 }}>
        {isFetching ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : warehouses.length === 0 ? (
          <Empty description="Chi nhánh chưa có kho nào" style={{ padding: 24 }} />
        ) : visibleWarehouses.length === 0 ? (
          <Empty description="Không tìm thấy kho phù hợp" style={{ padding: 24 }} />
        ) : (
          visibleWarehouses.map((w) => {
            const fulfilledCount = members.filter(
              (m) => qtyAt(w.warehouse_id, m.variantId) >= m.requiredQty,
            ).length;
            const isFull = fulfilledCount === members.length;
            const isNone = fulfilledCount === 0;
            const isSelected =
              members.length > 0 && members.every((m) => m.currentWarehouseId === w.warehouse_id);
            return (
              <div
                key={w.warehouse_id}
                onClick={() => {
                  if (!isNone) handlePick(w);
                }}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 12px',
                  borderBottom: '1px solid #f5f5f5',
                  cursor: isNone ? 'not-allowed' : 'pointer',
                  background: isSelected ? '#fff0f3' : '#fff',
                  opacity: isNone ? 0.55 : 1,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div>
                    <Text strong>{w.name}</Text>
                    {w.is_main_warehouse && <Tag style={{ marginLeft: 6 }}>Kho chính</Tag>}
                  </div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {w.code}
                  </Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <Tag color={isFull ? '#a4193d' : isNone ? 'default' : 'orange'} style={{ margin: 0 }}>
                    {isFull ? `Đủ tồn ${scopeNoun}` : isNone ? 'Không đủ tồn' : `Đủ ${fulfilledCount}/${members.length}`}
                  </Tag>
                  {isSelected && <CheckCircleFilled style={{ color: '#a4193d' }} />}
                </div>
              </div>
            );
          })
        )}
      </div>
    </Modal>
  );
}
